import { useState, useEffect } from 'react';
import { Alert, IconButton, Box, Typography, Select, MenuItem, Button, SelectChangeEvent, useTheme } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector, useAppDispatch } from '../../hooks/reduxHooks';
import { RootState } from '../../store';
import { useUploadFileToAIMutation } from '../../redux/ai/aiApi';
import ChatGPTWindow from '../ChatGPTWindow';
import { Handover } from '@handover/shared-types';
import { find } from 'lodash';
import { useNavigate } from 'react-router-dom';
import ChoosePdfButton from '../../helpers/ChooseFileButton';
import { ButtonLoading } from '../../components/commons';
import { setParserLoading, setParserError, addParserMessage } from '../../redux/ai/aiSlice';
import i18n from '../../locales/i18n';
import { HANDOVER_CAPSULE_COPY } from '../../constants/capsule';

interface ChatGPTHEParserProps {
  onClose: () => void;
}

const ChatGPTHEParser = ({ onClose }: ChatGPTHEParserProps) => {
  const [selectedHandover, setSelectedHandover] = useState<Handover | null>(null);
  const [uploadFileToAI] = useUploadFileToAIMutation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const dispatch = useAppDispatch();
  const handovers = useAppSelector((state: RootState) => state.handover.handoverDocData as Handover[]);
  const { parserLoading, parserError, parserMessages } = useAppSelector((state: RootState) => state.ai);

  const navigate = useNavigate();
  const theme = useTheme();

  // Clear errors when component mounts to handle cases where AI is re-enabled
  useEffect(() => {
    dispatch(setParserError(''));
    dispatch(addParserMessage(''));
  }, [dispatch]);

  const handleCreateHandover = () => {
    navigate('/handovers/create');
  };

  const handleHandoverSelect = (event: SelectChangeEvent<string>) => {
    const handoverId = event.target.value;
    if (handoverId === 'create') {
      handleCreateHandover();
    } else {
      const selected = find(handovers, handover => handover.id === handoverId);
      setSelectedHandover(selected || null);
    }
  };

  const handleAddHandover = async () => {
    if (!selectedHandover || !selectedFile) {
      dispatch(setParserError('Please select a target handover and a capsule file.'));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64File = reader.result?.toString().split(',')[1];
      const mimeType = selectedFile.type;

      try {
        if (base64File && selectedHandover?.id) {
          // Clear any previous errors before starting
          dispatch(setParserError(''));
          dispatch(setParserLoading(true));

          const response = await uploadFileToAI({
            file: base64File,
            handoverId: selectedHandover.id,
            mimeType,
          }).unwrap();

          dispatch(setParserLoading(false));
          // Clear error on successful response
          dispatch(setParserError(''));
          dispatch(addParserMessage(response.message));
          if (response.aiResponse) {
            navigate(`/handovers/${selectedHandover.id}`);
          }
        }
      } catch (err: any) {
        dispatch(setParserLoading(false));

        // Backend sends translation keys - translate them here
        if (err?.data?.messageKey) {
          const translatedMessage = String(i18n.t(err.data.messageKey, err.data.messageParams || {}));
          dispatch(setParserError(translatedMessage));
        } else if (err?.data?.message) {
          // Fallback: if backend sends direct message (legacy)
          dispatch(setParserError(err.data.message));
        } else {
          dispatch(setParserError('Failed to upload the capsule file. Please try again.'));
        }
      }
    };

    reader.readAsDataURL(selectedFile);
  };

  const handleFileSelect = (file: File | null, message?: string) => {
    dispatch(setParserError(''));
    dispatch(addParserMessage(''));

    if (file) {
      setSelectedFile(file);
    } else {
      dispatch(setParserError(message || 'File selection error.'));
    }
  };

  useEffect(() => {
    if (handovers.length > 0 && !selectedHandover) {
      setSelectedHandover(handovers[0]);
    }
  }, [handovers]);

  // Clear parser messages and errors after 4 seconds
  useEffect(() => {
    if (parserError || parserMessages) {
      const timeout = setTimeout(() => {
        dispatch(setParserError(''));
        dispatch(addParserMessage(''));
      }, 4000);

      return () => clearTimeout(timeout); // Cleanup timeout on component unmount
    }
  }, [parserError, parserMessages, dispatch]);

  return (
    <ChatGPTWindow
      default={{
        x: window.innerWidth / 2 - 250,
        y: window.innerHeight / 2 - 250,
        width: 700,
        height: 600,
      }}
    >
      <Box sx={styles.dialog}>
        <Box
          className="draggable-handle"
          sx={{
            ...styles.dialogTitle,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        >
          <Box sx={styles.titleText}>AI Draft From Handover Capsule</Box>
          <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>

        {handovers && handovers.length > 0 ? (
          <>
            <Alert severity="info" sx={{ mx: 2, mt: 2, mb: 0 }}>
              {HANDOVER_CAPSULE_COPY.summary} {HANDOVER_CAPSULE_COPY.currentState}
            </Alert>
            <Grid2
              container
              spacing={2}
              sx={{
                height: 'calc(100% - 196px)',
                padding: 2,
                display: 'flex',
              }}
            >
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Select
                  value={selectedHandover?.id || ''}
                  onChange={handleHandoverSelect}
                  displayEmpty
                  fullWidth
                  sx={styles.select}
                >
                  <MenuItem value="">
                    <em>SELECT TARGET HANDOVER</em>
                  </MenuItem>
                  {handovers.map((handover: Handover) => (
                    <MenuItem key={handover.id} value={handover.id}>
                      <strong>{handover.id}</strong>
                    </MenuItem>
                  ))}
                  <MenuItem value="create">
                    <em
                      style={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                      }}
                    >
                      CREATE TARGET HANDOVER
                    </em>
                  </MenuItem>
                </Select>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                {selectedHandover ? (
                  <>
                    <Typography variant="body2">
                      <strong>ID:</strong> {selectedHandover.id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Task:</strong> {selectedHandover.task}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created By:</strong> {selectedHandover.created?.createdBy}
                    </Typography>

                    {selectedFile && (
                      <Typography variant="body2" mt={2}>
                        <strong>Selected capsule file:</strong> {selectedFile.name}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2">Please select the handover that should receive the drafted output.</Typography>
                )}
              </Grid2>
            </Grid2>

            {parserError && <Typography color="error">{parserError}</Typography>}

            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <ChoosePdfButton onFileSelect={handleFileSelect} />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Manual uploads only. This capsule flow does not connect to Dropbox, Gmail, Outlook, Slack, or any other external system.
                </Typography>
              </Grid2>
            </Grid2>

            <Button
              variant="contained"
              color="primary"
              onClick={handleAddHandover}
              disabled={!selectedFile && true}
              sx={{
                ...styles.addButton,
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            >
              {parserLoading ? <ButtonLoading color="inherit" /> : <strong>Generate Draft From Capsule</strong>}
            </Button>

            <Typography color="primary" mt={2}>
              {parserMessages}
            </Typography>
          </>
        ) : (
          <Typography>No handovers available to receive capsule output.</Typography>
        )}
      </Box>
    </ChatGPTWindow>
  );
};

export default ChatGPTHEParser;

const styles = {
  dialog: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #ccc',
    padding: '8px',
    cursor: 'move',
  },
  titleText: {
    flex: 1,
    textAlign: 'left',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  closeButton: {
    color: 'inherit',
  },
  select: {
    marginBottom: '16px',
  },
  addButton: {
    width: '100%',
    marginTop: '16px',
  },
};
